/**
 * `settings/{doc}` — todo lo configurable va a Firestore, no al código (regla 6).
 * Documentos: fees, proPlans, featuredPrices, countries, legal, company, environment, featureFlags.
 * Solo admin (vía Functions) escribe; lectura pública de lo que el cliente necesita.
 */
import { z } from 'zod';
import { SELLER_LEVELS } from './users.js';
import {
  CountryCodeSchema,
  CurrencySchema,
  LanguageCodeSchema,
  LocalizedTextSchema,
  MoneySchema,
  TimestampSchema,
  UserIdSchema,
} from './primitives.js';
import { ProTierSchema } from './users.js';

export const SETTINGS_DOCS = [
  'fees',
  'proPlans',
  'featuredPrices',
  'countries',
  'legal',
  'company',
  'environment',
  'featureFlags',
  'supportStats',
  'socialPublisher',
  'adServer',
  'announcement',
  'club',
] as const;
export type SettingsDoc = (typeof SETTINGS_DOCS)[number];
export const settingsPath = (doc: SettingsDoc) => `settings/${doc}` as const;

/** Tarifas (modelo que NO cambia: 5 % + 0,70 € + 1,50 € transporte). */
export const FeesSettingsSchema = z.object({
  protection_fee_percent: z.number().min(0).max(100), // 5
  protection_fee_fixed: MoneySchema, // 70 céntimos EUR
  transport_surcharge: MoneySchema, // 150 céntimos EUR (decisión abierta 18.3)
  /** C7 — "en mano" con QR. */
  handover_fee: MoneySchema, // 99 céntimos EUR
  /**
   * Transcrito de la 1.0 (INSURANCE_MAX_PRICE_EUR = 500): a partir de este precio la protección
   * (5 % + fijo) NO se cobra; el recargo de transporte sí. `null` = sin umbral.
   */
  protection_fee_waived_above: MoneySchema.nullable(),
  /** L4: protección opcional desde el umbral, % del valor del artículo (10). */
  optional_protection_percent: z.number().min(0).max(100).optional(),
  /** C1 — pagar con saldo elimina la parte fija y el recargo. */
  wallet_payment_waives_fixed: z.boolean(),
  /**
   * Tipo FIJO por 1 EUR para convertir las tarifas fijas y el umbral a la moneda del pedido
   * (1.0 EUR_RATES): la estimación del cliente y el cobro del servidor usan la MISMA tabla.
   * No es el tipo de cambio de las ventas (ese es el de Stripe en el momento del pago).
   */
  fee_fx_rates: z.record(CurrencySchema, z.number().positive()),
  /** Decisión D7: recargo al vendedor solo si retira en una moneda distinta a la de su wallet. */
  fx_withdrawal_surcharge_percent: z.number().min(0).max(100),
  /** Retirada mínima (1.0: 1 €) y tope de aprobación automática (0 = todas pasan por admin, como hoy). */
  withdrawal_min: MoneySchema.optional(),
  withdrawal_auto_approve_max: MoneySchema.optional(),
  /**
   * Retirada instantánea (27/10): apagada hasta que Stripe dé a la plataforma un límite diario de
   * Instant Payouts (se pide a soporte de Stripe). Apagada = solo la estándar en web y apps.
   */
  instant_withdrawals_enabled: z.boolean().optional(),
  /** Comisión mínima del instantáneo cuando el nivel la cobra (Stripe cobra 1 %, mínimo 0,40 €). */
  instant_withdrawal_min_fee: MoneySchema.optional(),
  /** Devolución física en incidencias (7b): días para reenviar y días para confirmar la recepción. */
  dispute_return_days: z.number().int().positive().optional(),
  /** Ofertas: descuento máximo sobre el precio de lista (1.0: 40 % → la oferta mínima es el 60 %). */
  max_offer_discount_percent: z.number().min(0).max(100),
  /** Lotes: descuento máximo que puede configurar el vendedor (1.0: 50 %). */
  max_bundle_discount_percent: z.number().min(0).max(100),
  /** C2 — Nuvvu Club: puntos de descuento por tramo de compras. */
  club_tiers: z.array(
    z.object({ min_purchases: z.number().int().min(0), discount_points: z.number().min(0) }),
  ),
  /** 7b — plazos en horas. */
  accept_window_hours: z.number().int().positive(), // 48
  ship_window_hours: z.number().int().positive(), // 168
  dispute_window_hours: z.number().int().positive(), // 48
  dispute_seller_response_hours: z.number().int().positive(), // 72
  dispute_resolution_hours: z.number().int().positive(), // 168
  /** 7b — consecuencias: N cancelaciones en M días pausan anuncios K días. */
  seller_penalty: z.object({
    cancellations: z.number().int(),
    window_days: z.number().int(),
    pause_days: z.number().int(),
  }),
  /**
   * C3 — ventajas por nivel de vendedor (nuevo → fiable → top → elite): comisión del retiro
   * instantáneo (% del importe; 0 = gratis), subidas gratis al mes (las consume la monetización,
   * fase 3) y empuje en el feed (0–3, lo usa la ordenación por calidad, fase 3).
   */
  level_perks: z
    .record(
      z.enum(SELLER_LEVELS),
      z.object({
        instant_withdrawal_fee_percent: z.number().min(0).max(100),
        free_bumps_per_month: z.number().int().min(0),
        feed_boost: z.number().int().min(0).max(3),
      }),
    )
    .optional(),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type FeesSettings = z.infer<typeof FeesSettingsSchema>;

export const ProPlanSchema = z.object({
  tier: ProTierSchema,
  /** Clave de la 1.0 (`pro_plan_key`: pro, pro_plus, pro_max, pro_absoluto) — la usan la migración y RevenueCat. */
  legacy_key: z.enum(['pro', 'pro_plus', 'pro_max', 'pro_absoluto']),
  name: LocalizedTextSchema,
  /** Precio canónico en EUR; en otras monedas se deriva con el tipo del día y `cleanPrice` (como la 1.0). */
  monthly_price: MoneySchema,
  stripe_price_id: z.string().nullable(),
  app_store_product_id: z.string().nullable(),
  play_store_product_id: z.string().nullable(),
  photo_limit: z.number().int().positive(),
  /** Destacados incluidos al mes (1.0 `getProFeaturedMonthlyLimit`); `null` = sin límite. */
  featured_per_month: z.number().int().min(0).nullable(),
  /** Fotos al mes del estudio con IA (quitar arrugas, maniquí); 0 = no incluido. Solo PRO Absoluto (21/10). */
  ai_studio_per_month: z.number().int().min(0).default(0),
  /** Claves i18n `pro.benefit_*` (1.0 `PRO_TIER_BENEFIT_KEYS`). */
  benefits: z.array(z.string()),
  active: z.boolean(),
});
export type ProPlan = z.infer<typeof ProPlanSchema>;
export const ProPlansSettingsSchema = z.object({
  plans: z.array(ProPlanSchema).length(4),
  /** Sin PRO: fotos por anuncio (1.0: 10). */
  free_photo_limit: z.number().int().positive(),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type ProPlansSettings = z.infer<typeof ProPlansSettingsSchema>;

/** Catálogo de monetización (1.0 `FEATURE_PACKS`, `WARDROBE_SPOTLIGHT_PACK`, `BUMP_PACKS`). Precios canónicos en EUR. */
export const FeaturedPackSchema = z.object({
  code: z.string().min(1),
  /** `one` = un país; `all` = todos. */
  scope: z.enum(['one', 'all']),
  days: z.number().int().positive(),
  /** Además en portada. */
  homepage: z.boolean(),
  price: MoneySchema,
});
export const BumpPackSchema = z.object({
  code: z.string().min(1),
  /** 0 = una subida inmediata; N = subida ahora + auto-subida diaria N días (`dailyAutoBump`). */
  auto_days: z.number().int().min(0),
  price: MoneySchema,
});
export const SpotlightPackSchema = z.object({
  code: z.string().min(1),
  days: z.number().int().positive(),
  price: MoneySchema,
});
export const FeaturedPricesSettingsSchema = z.object({
  featured: z.array(FeaturedPackSchema).min(1),
  bumps: z.array(BumpPackSchema).min(1),
  /** Wardrobe Spotlight: perfil del vendedor destacado. */
  spotlight: z.array(SpotlightPackSchema).min(1),
  /** Días de spotlight semanal incluidos en PRO (1.0 `PRO_WARDROBE_SPOTLIGHT_DAYS`). */
  pro_spotlight_days: z.number().int().positive(),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type FeaturedPricesSettings = z.infer<typeof FeaturedPricesSettingsSchema>;

/** Sección 13b — un país activo. */
export const CountrySettingSchema = z.object({
  code: CountryCodeSchema,
  name: LocalizedTextSchema,
  default_language: LanguageCodeSchema,
  /** Idiomas que se ofrecen como opción al detectar el país (nunca forzados). */
  languages: z.array(LanguageCodeSchema).min(1),
  currency: CurrencySchema,
  phone_prefix: z.string().regex(/^\+\d{1,4}$/),
  /** Métodos de pago Stripe activables sin deploy (sección 14). */
  payment_methods: z.array(z.string()),
  /** UE-27 vs EEE/Schengen no UE (aduana al enviar desde/hacia la UE). */
  eu_member: z.boolean(),
  customs_required: z.boolean(),
  /** Puede registrarse / vender / comprar. La LECTURA pública nunca se bloquea (12b). */
  signup_enabled: z.boolean(),
  selling_enabled: z.boolean(),
  buying_enabled: z.boolean(),
});
export type CountrySetting = z.infer<typeof CountrySettingSchema>;

export const CountriesSettingsSchema = z.object({
  countries: z.array(CountrySettingSchema),
  /** Lista plana de códigos activos: la usan las REGLAS (`in` sobre un array) para validar direcciones. */
  codes: z.array(CountryCodeSchema),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type CountriesSettings = z.infer<typeof CountriesSettingsSchema>;

/**
 * `settings/socialPublisher` (1.0 platform_config/social_publisher; sin secretos — el token de Meta
 * y el app secret son `META_ACCESS_TOKEN` / `META_APP_SECRET` en Secret Manager, solo en producción).
 */
export const SocialPublisherSettingsSchema = z.object({
  enabled: z.boolean(),
  facebook_enabled: z.boolean(),
  instagram_enabled: z.boolean(),
  /** Horas (0–23) en `timezone` a las que publica el cron. */
  hours: z.array(z.number().int().min(0).max(23)).max(12),
  timezone: z.string().min(1),
  tone: z.enum(['fun', 'neutral']),
  mix: z.object({
    listing: z.number().min(0),
    seller: z.number().min(0),
    category: z.number().min(0),
    fact: z.number().min(0),
  }),
  blocked_categories: z.array(z.string()),
  min_price: z.number().min(0),
  hashtags: z.string().max(300),
  ig_link_comment: z.boolean(),
  /** Rotación de idiomas; vacía = idioma del país del anuncio. */
  languages: z.array(z.string().min(2).max(5)).max(33),
  /** Meta: ids públicos (no secretos). */
  page_id: z.string().max(40),
  ig_user_id: z.string().max(40),
  /** Lo lleva el servidor. */
  last_lang: z.string().nullable().optional(),
  last_run_slot: z.string().nullable().optional(),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type SocialPublisherSettings = z.infer<typeof SocialPublisherSettingsSchema>;

export const LegalSettingsSchema = z.object({
  /** Textos legales por documento e idioma, editables desde admin. */
  documents: z.record(
    z.enum(['terms', 'privacy', 'cookies', 'legal_notice', 'protection_policy', 'how_we_rank']),
    z.object({ version: z.string(), content: LocalizedTextSchema, published_at: TimestampSchema }),
  ),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});

/** Redes del "Síguenos" (1.0 `platform_config/company_info.value.social`); solo https. */
export const SOCIAL_NETWORKS = [
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'x',
  'linkedin',
  'pinterest',
  'threads',
  'telegram',
  'whatsapp',
] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export const CompanySettingsSchema = z.object({
  legal_name: z.string(), // Infinity Advance SL
  vat_number: z.string(),
  /** NIF-IVA intracomunitario (VIES), p. ej. ESB09669714. */
  eu_vat_id: z.string().optional(),
  /** Aviso legal (LSSI art. 10): representante y datos del Registro Mercantil. Si faltan, la frase no se pinta. */
  legal_representative: z.string().optional(),
  registry_volume: z.string().optional(),
  registry_page: z.string().optional(),
  registry_sheet: z.string().optional(),
  /** Mediador de consumo para Francia (L13; mentions légales §4). */
  fr_mediator_name: z.string().optional(),
  fr_mediator_address: z.string().optional(),
  fr_mediator_website: z.string().url().optional(),
  address: z.string(),
  jurisdiction: z.string(),
  founded: z.string(),
  website: z.string().url(),
  support_email: z.string().email(), // info@nuvvu.eu
  sender_email: z.string().email(), // hola@app.nuvvu.eu
  dsa_contact_email: z.string().email(),
  /** Quién recibe los avisos del admin por correo (ventas, tickets, disputas, resumen de las 8:00…). */
  admin_alert_emails: z.array(z.string().email()).max(10).optional(),
  /** Enlaces de redes (solo los que existan); el front no muestra la tira si está vacío. */
  social: z.record(z.enum(SOCIAL_NETWORKS), z.string().url().startsWith('https://')),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type CompanySettings = z.infer<typeof CompanySettingsSchema>;

/** Lo lee el front para pintar el banner "SANDBOX" (sección 5). */
export const EnvironmentSettingsSchema = z.object({
  env: z.enum(['sandbox', 'prod']),
  maintenance: z.boolean(),
  maintenance_message: LocalizedTextSchema.nullable(),
  min_app_version: z.object({ android: z.string(), ios: z.string() }),
});
export type EnvironmentSettings = z.infer<typeof EnvironmentSettingsSchema>;

/**
 * Aviso emergente para todos los usuarios (1.0 `platform_config/announcement` + AnnouncementModal):
 * una versión nueva, una alerta de estafas, un cambio de tarifas. Cambiar `id_key` = volver a
 * enseñarlo a todo el mundo. Quien lo cierra queda apuntado en `users.dismissed_announcements`
 * (registrados) o en el navegador (invitados). Texto por idioma; español de respaldo (T17).
 */
export const AnnouncementContentSchema = z.object({
  title: z.string().max(120),
  body: z.string().max(2000),
  cta_label: z.string().max(60).optional(),
  cta_url: z.string().url().startsWith('https://').optional(),
  close_label: z.string().max(40).optional(),
});
export const AnnouncementSettingsSchema = z.object({
  id_key: z.string().min(1).max(60),
  active: z.boolean(),
  severity: z.enum(['info', 'warning']),
  dismissible: z.boolean(),
  /** 'all' incluye visitantes; 'sellers' = con anuncio activo; 'buyers' = con sesión y sin anuncios. */
  audience: z.enum(['all', 'logged_in', 'sellers', 'buyers']),
  /** ISO 8601 o null. */
  starts_at: z.string().nullable(),
  ends_at: z.string().nullable(),
  content: z.record(LanguageCodeSchema, AnnouncementContentSchema),
});
export type AnnouncementSettings = z.infer<typeof AnnouncementSettingsSchema>;

/** Interruptores sin deploy (sección 11: cada función de IA tiene uno). */
export const FeatureFlagsSettingsSchema = z.object({
  flags: z.record(z.string(), z.boolean()),
  /**
   * 2.1 §6: tercer estado «solo admins». Con `flags[k] = false` y `admin_only[k] = true` la función
   * solo existe para los administradores (pruebas en producción con datos reales). Las apps 2.0.x
   * leen solo `flags`, así que para ellas sigue apagada.
   */
  admin_only: z.record(z.string(), z.boolean()).optional(),
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type FeatureFlagsSettings = z.infer<typeof FeatureFlagsSettingsSchema>;
